import type { ResolvedConcept } from '@/libs/contentful/rest/fetchPageREST';

/**
 * Finds the most specific (deepest / leaf) concept from a set of concept IDs.
 *
 * Among the provided concepts, the "most specific" is the one that is NOT
 * a parent of any other concept in the set — i.e. the leaf in the hierarchy.
 * When multiple leaves exist (siblings at the same depth), the last one in the
 * array is preferred, as the most recently tagged concept is typically the
 * intended primary category.
 */
export function getMostSpecificConcept(
  conceptIds: string[],
  conceptMap: Map<string, ResolvedConcept>,
): ResolvedConcept | undefined {
  if (conceptIds.length === 0) return undefined;

  const resolved = conceptIds.map(id => conceptMap.get(id)).filter(Boolean);
  if (resolved.length === 0) return undefined;
  if (resolved.length === 1) return resolved[0];

  const articleConceptSet = new Set(conceptIds);
  const parentIds = new Set<string>();
  for (const id of conceptIds) {
    const concept = conceptMap.get(id);
    if (concept) {
      for (const broaderId of concept.broaderIds) {
        if (articleConceptSet.has(broaderId)) {
          parentIds.add(broaderId);
        }
      }
    }
  }

  const leaves = conceptIds.filter(id => !parentIds.has(id) && conceptMap.has(id));
  const leafId = leaves[leaves.length - 1];
  return leafId ? conceptMap.get(leafId) : resolved[resolved.length - 1];
}
